import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { useScreenInit } from './useScreenInit';
import { StickyAlertBar } from './components/StickyAlertBar';
import { HeroSection } from './components/HeroSection';
import { SalesLetter } from './components/SalesLetter';
// Below-the-fold sections are code-split so the initial bundle only carries
// what's needed for the first paint (alert bar + hero + start of the letter).
const WhatYouGet = lazy(() => import('./components/WhatYouGet').then(m => ({
  default: m.WhatYouGet
})));
const SocialProof = lazy(() => import('./components/SocialProof').then(m => ({
  default: m.SocialProof
})));
const IncludesStack = lazy(() => import('./components/IncludesStack').then(m => ({
  default: m.IncludesStack
})));
const BonusSection = lazy(() => import('./components/BonusSection').then(m => ({
  default: m.BonusSection
})));
const PricingOffer = lazy(() => import('./components/PricingOffer').then(m => ({
  default: m.PricingOffer
})));
const Guarantee = lazy(() => import('./components/Guarantee').then(m => ({
  default: m.Guarantee
})));
const FaqSection = lazy(() => import('./components/FaqSection').then(m => ({
  default: m.FaqSection
})));
const FinalCta = lazy(() => import('./components/FinalCta').then(m => ({
  default: m.FinalCta
})));
const SiteFooter = lazy(() => import('./components/SiteFooter').then(m => ({
  default: m.SiteFooter
})));
// Reserves vertical space while a lazy chunk loads to avoid layout shift.
function SectionFallback() {
  return <div className="min-h-[40vh] w-full" aria-hidden="true" data-id="element-0" data-mp="38l8eviz7tido" />;
}
export function App() {
  useScreenInit();
  // Preconnect to font/image origins early (non-blocking).
  useEffect(() => {
    const origins = ['https://fonts.googleapis.com', 'https://fonts.gstatic.com', 'https://images.unsplash.com'];
    origins.forEach(href => {
      if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = '';
      document.head.appendChild(link);
    });
  }, []);
  // Meta Pixel — deferred to idle so it never blocks the first render.
  useEffect(() => {
    const load = () => {
      const w = window as any;
      if (!w.fbq) {
        const n: any = w.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!w._fbq) w._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        document.head.appendChild(script);
        w.fbq('init', '1724342565264992');
      }
      w.fbq('track', 'PageView');
    };
    const ric = (window as any).requestIdleCallback as ((cb: () => void, opts?: {
      timeout: number;
    }) => number) | undefined;
    if (ric) {
      ric(load, {
        timeout: 3000
      });
    } else {
      const t = setTimeout(load, 1500);
      return () => clearTimeout(t);
    }
  }, []);
  // Document title + social meta tags.
  useEffect(() => {
    const title = 'Pollería Rentable 2026 — Montá y Hacé Rentable Tu Pollería en 30 Días';
    const description = 'El sistema con +20 videos, calculadoras y trámites resueltos para poner o hacer rentable tu pollería, hecho por un pollero real.';
    document.title = title;
    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
  }, []);
  return <div className="min-h-full w-full bg-cream font-sans text-ink" data-id="element-1" data-mp="ca2i1pifwbm3">
      <StickyAlertBar data-id="element-2" data-mp="19uyll7conab1" />
      <main data-id="element-3" data-mp="19vhtdv8mcibc">
        {/* B. Hero (the hook) — eager, above the fold */}
        <HeroSection data-id="element-4" data-mp="14xyd6psd1vgr" />

        {/* C. Sales letter — pains (red) + solutions (green) */}
        <SalesLetter data-id="element-5" data-mp="zx29ac80e9bc" />

        {/* Below-the-fold sections load lazily (code-split) */}
        <Suspense fallback={<SectionFallback data-id="element-7" data-mp="vv706lju6rn0" />} data-id="element-6" data-mp="vwrv4ca2ccle">
          {/* D. What you get — alternating feature stack */}
          <WhatYouGet data-id="element-8" data-mp="3kob2gaat5w36" />

          {/* G. Social proof — masonry testimonials */}
          <SocialProof data-id="element-9" data-mp="3gly0zy33gkc7" />

          {/* Includes recap */}
          <IncludesStack data-id="element-10" data-mp="3gok7rvi5uvtd" />

          {/* E. Bonus value stack */}
          <BonusSection data-id="element-11" data-mp="1w5ghv1xpig9j" />

          {/* F. Dynamic pricing offer */}
          <PricingOffer data-id="element-12" data-mp="205ihz6nsn1mp" />

          {/* Guarantee */}
          <Guarantee data-id="element-13" data-mp="206a1hj4498wm" />

          {/* FAQ */}
          <FaqSection data-id="element-14" data-mp="254ld9zdz1thh" />

          {/* Final CTA + scarcity + P.D. */}
          <FinalCta data-id="element-15" data-mp="253a6rrt567mo" />

          {/* Footer */}
          <SiteFooter data-id="element-16" data-mp="29wv8nuxuvlr5" />
        </Suspense>
      </main>
    </div>;
}